import { useState, useMemo, useRef, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  LineChart, Line,
} from "recharts";
import { Loader2, TrendingUp, TrendingDown, Trophy, Target, Brain, ArrowUpRight, ArrowDownRight, Download, ClipboardList, BarChart3 } from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { ar } from "date-fns/locale";
import ComprehensiveTracking from "@/components/analytics/ComprehensiveTracking";

const chartConfig = {
  score: { label: "النتيجة", color: "hsl(var(--primary))" },
  average: { label: "المتوسط", color: "hsl(var(--secondary))" },
};

const ChildAnalytics = () => {
  const { profile } = useAuth();
  const nurseryId = profile?.nursery_id;
  const [selectedChildId, setSelectedChildId] = useState<string>("");
  const [exporting, setExporting] = useState(false);
  const reportRef = useRef<HTMLDivElement>(null);

  // Fetch children
  const { data: children, isLoading: childrenLoading } = useQuery({
    queryKey: ["analytics-children", nurseryId],
    queryFn: async () => {
      const { data } = await supabase
        .from("children")
        .select("id, full_name, class_id, classes(name)")
        .eq("nursery_id", nurseryId!)
        .order("full_name");
      return data || [];
    },
    enabled: !!nurseryId,
  });

  // Fetch all submissions for selected child
  const { data: submissions, isLoading: subsLoading } = useQuery({
    queryKey: ["child-submissions", selectedChildId],
    queryFn: async () => {
      const { data } = await supabase
        .from("exam_submissions")
        .select("*, exams(title, class_id, created_at)")
        .eq("child_id", selectedChildId)
        .eq("status", "completed")
        .order("submitted_at", { ascending: true });
      return data || [];
    },
    enabled: !!selectedChildId,
  });

  // Fetch all answers for selected child's submissions
  const { data: answers } = useQuery({
    queryKey: ["child-answers", selectedChildId],
    queryFn: async () => {
      if (!submissions || submissions.length === 0) return [];
      const subIds = submissions.map((s: any) => s.id);
      const { data } = await supabase
        .from("exam_answers")
        .select("*, questions(subject, question_type, difficulty)")
        .in("submission_id", subIds);
      return data || [];
    },
    enabled: !!submissions && submissions.length > 0,
  });

  // Calculate subject performance
  const subjectStats = useMemo(() => {
    if (!answers || answers.length === 0) return [];
    const subjects: Record<string, { correct: number; total: number }> = {};
    answers.forEach((a: any) => {
      const subject = (a.questions as any)?.subject || "عام";
      if (!subjects[subject]) subjects[subject] = { correct: 0, total: 0 };
      subjects[subject].total++;
      if (a.is_correct) subjects[subject].correct++;
    });
    return Object.entries(subjects).map(([name, s]) => ({
      subject: name,
      score: Math.round((s.correct / s.total) * 100),
      correct: s.correct,
      total: s.total,
    })).sort((a, b) => b.score - a.score);
  }, [answers]);

  // Strengths and weaknesses
  const strengths = subjectStats.filter((s) => s.score >= 70);
  const weaknesses = subjectStats.filter((s) => s.score < 70);

  // Performance over time (line chart)
  const timelineData = useMemo(() => {
    if (!submissions || submissions.length === 0) return [];
    return submissions.map((s: any) => ({
      date: s.submitted_at ? format(new Date(s.submitted_at), "d MMM", { locale: ar }) : "",
      score: s.percentage || 0,
      exam: (s.exams as any)?.title || "",
    }));
  }, [submissions]);

  // Difficulty breakdown
  const difficultyStats = useMemo(() => {
    if (!answers || answers.length === 0) return [];
    const diffs: Record<string, { correct: number; total: number }> = {};
    answers.forEach((a: any) => {
      const diff = (a.questions as any)?.difficulty || "medium";
      const label = diff === "easy" ? "سهل" : diff === "medium" ? "متوسط" : "صعب";
      if (!diffs[label]) diffs[label] = { correct: 0, total: 0 };
      diffs[label].total++;
      if (a.is_correct) diffs[label].correct++;
    });
    return Object.entries(diffs).map(([name, d]) => ({
      difficulty: name,
      score: Math.round((d.correct / d.total) * 100),
      correct: d.correct,
      total: d.total,
    }));
  }, [answers]);

  // Question type breakdown
  const typeStats = useMemo(() => {
    if (!answers || answers.length === 0) return [];
    const types: Record<string, { correct: number; total: number }> = {};
    answers.forEach((a: any) => {
      const type = (a.questions as any)?.question_type || "multiple_choice";
      const label = type === "multiple_choice" ? "اختيار متعدد" : type === "true_false" ? "صح/خطأ" : "مقالي";
      if (!types[label]) types[label] = { correct: 0, total: 0 };
      types[label].total++;
      if (a.is_correct) types[label].correct++;
    });
    return Object.entries(types).map(([name, t]) => ({
      type: name,
      score: Math.round((t.correct / t.total) * 100),
    }));
  }, [answers]);

  const overallAvg = submissions && submissions.length > 0
    ? Math.round(submissions.reduce((sum: number, s: any) => sum + (s.percentage || 0), 0) / submissions.length)
    : 0;

  const latestScore = submissions && submissions.length > 0
    ? (submissions[submissions.length - 1] as any).percentage || 0
    : 0;

  const previousScore = submissions && submissions.length > 1
    ? (submissions[submissions.length - 2] as any).percentage || 0
    : null;

  const trend = previousScore !== null ? latestScore - previousScore : 0;

  const selectedChild = children?.find((c: any) => c.id === selectedChildId);
  const isLoading = childrenLoading || subsLoading;

  const handleExportPDF = useCallback(async () => {
    if (!reportRef.current || !selectedChild) return;
    setExporting(true);
    try {
      const html2canvas = (await import("html2canvas")).default;
      const { jsPDF } = await import("jspdf");

      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        logging: false,
      });

      const imgData = canvas.toDataURL("image/png");
      const imgWidth = 190;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      const pdf = new jsPDF("p", "mm", "a4");
      const pageHeight = 277;
      let position = 10;

      // Title
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(14);
      pdf.text(`Performance Report - ${selectedChild.full_name}`, 105, position, { align: "center" });
      position += 8;
      pdf.setFontSize(9);
      pdf.setFont("helvetica", "normal");
      pdf.text(format(new Date(), "d MMM yyyy", { locale: ar }), 105, position, { align: "center" });
      position += 8;

      // Add image pages
      let heightLeft = imgHeight;
      pdf.addImage(imgData, "PNG", 10, position, imgWidth, imgHeight);
      heightLeft -= (pageHeight - position);

      while (heightLeft > 0) {
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 10, -(imgHeight - heightLeft), imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`report-${selectedChild.full_name}-${format(new Date(), "yyyy-MM-dd")}.pdf`);
    } catch (e) {
      console.error("PDF export failed:", e);
    } finally {
      setExporting(false);
    }
  }, [selectedChild]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">تحليلات أداء الأطفال</h1>
          <p className="text-muted-foreground text-sm mt-1">تحليل شامل لنقاط القوة والضعف في كل مادة</p>
        </div>
      </div>

      <Tabs defaultValue="academic" className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="academic" className="gap-2">
            <BarChart3 className="w-4 h-4" />
            التحليلات الأكاديمية
          </TabsTrigger>
          <TabsTrigger value="comprehensive" className="gap-2">
            <ClipboardList className="w-4 h-4" />
            المتابعة الشاملة
          </TabsTrigger>
        </TabsList>

        <TabsContent value="academic" className="mt-6 space-y-6">
          {/* Child selector + export */}
          <div className="flex items-center gap-2 justify-end">
            {selectedChildId && submissions && submissions.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportPDF}
                disabled={exporting}
                className="gap-2"
              >
                {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                تصدير PDF
              </Button>
            )}
            <Select value={selectedChildId} onValueChange={setSelectedChildId}>
              <SelectTrigger className="w-[220px]">
                <SelectValue placeholder="اختر طفل..." />
              </SelectTrigger>
              <SelectContent>
                {(children || []).map((c: any) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.full_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {!selectedChildId ? (
            <div className="text-center py-20">
              <Brain className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
              <p className="text-muted-foreground">اختر طفل لعرض تحليلات أدائه</p>
            </div>
          ) : isLoading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : !submissions || submissions.length === 0 ? (
            <div className="text-center py-20">
              <Target className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
              <p className="text-muted-foreground">لا توجد نتائج امتحانات لهذا الطفل بعد</p>
            </div>
          ) : (
            <div ref={reportRef} className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  {
                    label: "المعدل العام",
                    value: `${overallAvg}%`,
                    icon: Trophy,
                    color: overallAvg >= 70 ? "text-primary" : "text-destructive",
                  },
                  {
                    label: "عدد الامتحانات",
                    value: submissions.length,
                    icon: Target,
                    color: "text-secondary",
                  },
                  {
                    label: "آخر نتيجة",
                    value: `${latestScore}%`,
                    icon: latestScore >= 70 ? TrendingUp : TrendingDown,
                    color: latestScore >= 70 ? "text-primary" : "text-destructive",
                  },
                  {
                    label: "التطور",
                    value: trend > 0 ? `+${trend}%` : `${trend}%`,
                    icon: trend >= 0 ? ArrowUpRight : ArrowDownRight,
                    color: trend >= 0 ? "text-primary" : "text-destructive",
                  },
                ].map((s, i) => (
                  <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                    <Card className="shadow-card">
                      <CardContent className="p-4 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
                          <s.icon className={`w-5 h-5 ${s.color}`} />
                        </div>
                        <div>
                          <div className="text-xl font-bold text-foreground">{s.value}</div>
                          <div className="text-xs text-muted-foreground">{s.label}</div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Radar Chart */}
                {subjectStats.length > 0 && (
                  <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
                    <Card className="shadow-card">
                      <CardHeader>
                        <CardTitle className="text-lg">أداء المواد</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ChartContainer config={chartConfig} className="h-[300px] w-full">
                          <RadarChart data={subjectStats} cx="50%" cy="50%" outerRadius="75%">
                            <PolarGrid stroke="hsl(var(--border))" />
                            <PolarAngleAxis dataKey="subject" className="text-xs" />
                            <PolarRadiusAxis angle={30} domain={[0, 100]} tick={false} />
                            <Radar
                              name="النتيجة"
                              dataKey="score"
                              stroke="hsl(var(--primary))"
                              fill="hsl(var(--primary))"
                              fillOpacity={0.25}
                              strokeWidth={2}
                            />
                            <ChartTooltip content={<ChartTooltipContent />} />
                          </RadarChart>
                        </ChartContainer>
                      </CardContent>
                    </Card>
                  </motion.div>
                )}

                {/* Performance Over Time */}
                {timelineData.length > 1 && (
                  <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                    <Card className="shadow-card">
                      <CardHeader>
                        <CardTitle className="text-lg">تطور الأداء عبر الزمن</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ChartContainer config={chartConfig} className="h-[300px] w-full">
                          <LineChart data={timelineData}>
                            <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                            <XAxis dataKey="date" className="text-xs" />
                            <YAxis domain={[0, 100]} unit="%" />
                            <ChartTooltip
                              content={({ active, payload }) => {
                                if (!active || !payload?.length) return null;
                                const d = payload[0].payload;
                                return (
                                  <div className="bg-popover border rounded-lg p-2 shadow-md text-xs">
                                    <p className="font-medium">{d.exam}</p>
                                    <p className="text-primary font-bold">{d.score}%</p>
                                  </div>
                                );
                              }}
                            />
                            <Line
                              type="monotone"
                              dataKey="score"
                              stroke="hsl(var(--primary))"
                              strokeWidth={2.5}
                              dot={{ fill: "hsl(var(--primary))", r: 4 }}
                              activeDot={{ r: 6 }}
                            />
                          </LineChart>
                        </ChartContainer>
                      </CardContent>
                    </Card>
                  </motion.div>
                )}

                {/* Difficulty Breakdown */}
                {difficultyStats.length > 0 && (
                  <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
                    <Card className="shadow-card">
                      <CardHeader>
                        <CardTitle className="text-lg">الأداء حسب مستوى الصعوبة</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ChartContainer config={chartConfig} className="h-[250px] w-full">
                          <BarChart data={difficultyStats} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                            <XAxis type="number" domain={[0, 100]} unit="%" />
                            <YAxis dataKey="difficulty" type="category" width={60} />
                            <ChartTooltip content={<ChartTooltipContent />} />
                            <Bar
                              dataKey="score"
                              fill="hsl(var(--primary))"
                              radius={[0, 6, 6, 0]}
                              name="النتيجة"
                            />
                          </BarChart>
                        </ChartContainer>
                      </CardContent>
                    </Card>
                  </motion.div>
                )}

                {/* Strengths & Weaknesses */}
                <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
                  <Card className="shadow-card h-full">
                    <CardHeader>
                      <CardTitle className="text-lg">نقاط القوة والضعف</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-5">
                      <div>
                        <h4 className="text-sm font-semibold text-primary mb-3 flex items-center gap-2">
                          <TrendingUp className="w-4 h-4" /> نقاط القوة
                        </h4>
                        {strengths.length === 0 ? (
                          <p className="text-xs text-muted-foreground">لا توجد مواد بنتيجة 70% أو أعلى</p>
                        ) : (
                          <div className="space-y-2.5">
                            {strengths.map((s, i) => (
                              <div key={i}>
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-sm font-medium">{s.subject}</span>
                                  <Badge variant="outline" className="text-xs text-primary border-primary/30">
                                    {s.score}% ({s.correct}/{s.total})
                                  </Badge>
                                </div>
                                <Progress value={s.score} className="h-2" />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <div>
                        <h4 className="text-sm font-semibold text-destructive mb-3 flex items-center gap-2">
                          <TrendingDown className="w-4 h-4" /> نقاط الضعف
                        </h4>
                        {weaknesses.length === 0 ? (
                          <p className="text-xs text-muted-foreground">ممتاز! لا توجد نقاط ضعف</p>
                        ) : (
                          <div className="space-y-2.5">
                            {weaknesses.map((s, i) => (
                              <div key={i}>
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-sm font-medium">{s.subject}</span>
                                  <Badge variant="outline" className="text-xs text-destructive border-destructive/30">
                                    {s.score}% ({s.correct}/{s.total})
                                  </Badge>
                                </div>
                                <Progress value={s.score} className="h-2 [&>div]:bg-destructive" />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {typeStats.length > 0 && (
                        <div>
                          <h4 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
                            <Brain className="w-4 h-4" /> حسب نوع السؤال
                          </h4>
                          <div className="space-y-2.5">
                            {typeStats.map((t, i) => (
                              <div key={i}>
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-sm">{t.type}</span>
                                  <span className="text-xs text-muted-foreground font-medium">{t.score}%</span>
                                </div>
                                <Progress value={t.score} className="h-1.5 [&>div]:bg-secondary" />
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              </div>

              {/* Exam Details Table */}
              <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}>
                <Card className="shadow-card">
                  <CardHeader>
                    <CardTitle className="text-lg">سجل الامتحانات</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b text-muted-foreground">
                            <th className="text-right py-2 pr-2 font-medium">الامتحان</th>
                            <th className="text-center py-2 font-medium">النتيجة</th>
                            <th className="text-center py-2 font-medium">الدرجة</th>
                            <th className="text-right py-2 font-medium">التاريخ</th>
                          </tr>
                        </thead>
                        <tbody>
                          {submissions.map((s: any) => (
                            <tr key={s.id} className="border-b last:border-0 hover:bg-muted/30">
                              <td className="py-2.5 pr-2 font-medium">{(s.exams as any)?.title || "—"}</td>
                              <td className="text-center">
                                <Badge
                                  variant="outline"
                                  className={
                                    (s.percentage || 0) >= 70
                                      ? "text-primary border-primary/30"
                                      : (s.percentage || 0) >= 50
                                      ? "text-yellow-600 border-yellow-300"
                                      : "text-destructive border-destructive/30"
                                  }
                                >
                                  {s.percentage || 0}%
                                </Badge>
                              </td>
                              <td className="text-center text-muted-foreground">
                                {s.total_score}/{s.max_score}
                              </td>
                              <td className="text-right text-muted-foreground text-xs">
                                {s.submitted_at
                                  ? format(new Date(s.submitted_at), "d MMM yyyy", { locale: ar })
                                  : "—"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="comprehensive" className="mt-6">
          {nurseryId ? (
            <ComprehensiveTracking nurseryId={nurseryId} />
          ) : (
            <div className="text-center py-20">
              <p className="text-muted-foreground">لا يمكن عرض البيانات بدون حضانة مرتبطة</p>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ChildAnalytics;
